import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import Button from 'components/Button';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import Table, { IndeterminateCheckbox } from 'components/Table';
import Title from 'components/Title';
import { customPaddingStyles } from 'containers/Integrations/shared/react-select-custom';
import { IBulkMapUnmapADPWorkers, useBulkMapUnmapADPWorkers } from 'hooks/integrations/useBulkMapUnmapADPWorkers';
import { useFetchIntegrationSyncStatusById } from 'hooks/integrations/useFetchIntegrationSyncStatusById';
import {
  fetchADPTimeeroPaginatedEmployee,
  IADPTimeeroEmployeeSearchQuery,
} from 'hooks/integrations/useFetchPaginatedADPTimeeroEmployee';
import { useFetchADPPaginatedWorkers } from 'hooks/integrations/useFetchPaginatedADPWorkers';
import { useImportADPWorkers, IADPWorkers } from 'hooks/integrations/useImportADPWorkers';
import { useMapADPWorker, IADPMapWorker } from 'hooks/integrations/useMapADPWorkers';
import { useSyncADPWorker } from 'hooks/integrations/useSyncADPWorker';
import { IAdpWfnWorkUnmap, useUnmapAdpWfnWorker } from 'hooks/integrations/useUnMapAdpWfnWorker';
import { getAsyncSelectLoadOptions } from 'utils/helpers';
import toaster from 'utils/toaster';

import WorkersMappingTableFooter from './WorkersMappingTableFooter';

const initialSearchQuery: IADPTimeeroEmployeeSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const WorkersMapping = () => {
  const { t } = useTranslation(['integrations']);

  const syncLongPollingStatus = useRef<'idle' | 'fetching'>('idle');

  const [mappedUsers, setMappedUsers] = useState<any>(null);
  const [currentMapUser, setCurrentMapUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<IADPTimeeroEmployeeSearchQuery>(initialSearchQuery);

  const {
    data: adpWorkerData,
    status: adpWorkerStatus,
    isFetching: isADPWorkerFetching,
    refetch,
  } = useFetchADPPaginatedWorkers(searchQuery);

  const { status: adpWorkerMapStatus, mutateAsync: adpWorkerMutateAsync } = useMapADPWorker();
  const { status: adpImportWorkerStatus, mutateAsync: adpImportWorkerMutateAsync } = useImportADPWorkers();
  const { status: adpSyncStatus, mutateAsync: adpSyncMutateAsync, data } = useSyncADPWorker();
  const { status: adpWfnWorkerUnmapStatus, mutateAsync: adpWfnWorkerUnmapMutateAsync } = useUnmapAdpWfnWorker();
  const { status: bulkMapUnmapWorkerStatus, mutateAsync: mutateAsyncBulkMapUnmapWorkers } = useBulkMapUnmapADPWorkers();

  useFetchIntegrationSyncStatusById(data?.payload?.id, {
    enabled: syncLongPollingStatus.current === 'fetching' && data?.payload?.id ? true : false,
    refetchInterval: (response: any) => {
      if (response?.payload?.finished === true) {
        if (syncLongPollingStatus.current === 'fetching') {
          refetch();
          toaster.success({ description: response?.message || 'Synced Successfully.' });
        }
        syncLongPollingStatus.current = 'idle';
        return false;
      }

      return 3000;
    },
  });

  const columns = [
    {
      id: 'selection',
      width: 30,
      Header: ({ getToggleAllRowsSelectedProps }: any) => {
        return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} colorScheme="secondary" />;
      },
      Cell: ({ row }: any) => {
        return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} colorScheme="secondary" />;
      },
    },
    {
      Header: 'ADP Workers',
      accessor: 'name',
      width: 220,
      Cell: (data: CellValue) => {
        return (
          <Box>
            <Text textOverflow="ellipsis" overflow="hidden" mb={1}>
              {data?.row?.original?.full_name}
            </Text>
            <Text textOverflow="ellipsis" overflow="hidden" color="gray.500">
              {data?.row?.original?.email}
            </Text>
          </Box>
        );
      },
    },
    {
      Header: 'Employee Type',
      accessor: 'remuneration_type',
    },
    {
      Header: 'Payroll Group',
      accessor: 'payrollGroupCode',
    },
    {
      Header: 'Status',
      accessor: 'status',
      width: 80,
    },
    {
      Header: 'Timeero Employees',
      accessor: 'role',
      width: 220,
      Cell: (data: CellValue) => {
        return (
          <Box position="relative" width="100%">
            {adpWorkerMapStatus === 'loading' && currentMapUser === data.row.original.id && (
              <Flex
                width="100%"
                height="100%"
                position="absolute"
                zIndex={2}
                justifyContent="center"
                alignItems="center"
                background="rgba(255, 255, 255, 0.7)"
              >
                <Spinner size="xs" />
              </Flex>
            )}
            <ReactSelectAsyncPaginate
              loadOptions={timeeroEmployeeLoadOptions}
              additional={{
                page: 1,
              }}
              value={
                data.row.original?.selected_user
                  ? data.row.original?.selected_user || null
                  : mappedUsers &&
                      mappedUsers[`${data.row.original.id}`] &&
                      mappedUsers[`${data.row.original.id}`].employee
                    ? mappedUsers[`${data.row.original.id}`].employee
                    : null
              }
              onChange={(value: any) => {
                setMappedUsers((originalValues: any) => {
                  const prevValues = originalValues ? { ...originalValues } : null;

                  if (prevValues) {
                    const oldValue = Object.entries(prevValues).find(([_, prevValue]: any) => {
                      return prevValue?.employee?.id === value.id;
                    });

                    if (oldValue) {
                      delete prevValues[oldValue[0]];
                    }
                  }

                  return {
                    ...prevValues,
                    [data.row.original.id]: {
                      employee: value || null,
                    },
                  };
                });
              }}
              size="md"
              variant="filled"
              fontSize="sm"
              width="100%"
              debounceTimeout={300}
              menuPortalTarget={document.body}
              isSearchable
              placeholder="Select employee"
              formatOptionLabel={formatOptionLabel}
              getOptionLabel={(option: any) => option.full_name}
              getOptionValue={(option: any) => option.id}
              styles={customPaddingStyles}
            />
          </Box>
        );
      },
    },
    {
      Header: '',
      accessor: 'first',
      width: 180,
      Cell: (data: CellValue) => {
        return data.row.original.selected_user ? (
          <Button
            _hover={{ bg: 'secondary.100' }}
            _active={{ bg: 'secondary.100' }}
            fontWeight="bold"
            background="rgba(250, 85, 85, 0.2)"
            color="rgba(250, 85, 85, 1)"
            isLoading={adpWfnWorkerUnmapStatus === 'loading' && currentMapUser === data.row.original.id}
            onClick={() => onUnMapADPWorker(data.row.original)}
          >
            {t('integrations:removeMapping')}
          </Button>
        ) : (
          <Button
            _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
            background="rgba(255, 103, 32, 0.1)"
            colorScheme="rgba(255, 103, 32, 0.3)"
            color="secondary.500"
            isLoading={adpWorkerMapStatus === 'loading' && currentMapUser === data.row.original.id}
            onClick={() => onMapADPWorker(data.row.original)}
          >
            {t('integrations:mapEmployee')}
          </Button>
        );
      },
    },
  ];

  const formatOptionLabel = (option: any) => {
    return (
      <Box>
        <Text fontSize="sm" fontWeight={600}>
          {option.full_name}
        </Text>
        <Text fontSize="sm" fontWeight={400}>
          {option.email}
        </Text>
      </Box>
    );
  };

  const onMapADPWorker = ({ id, associateOID }: any) => {
    if (mappedUsers && mappedUsers[id] && mappedUsers[id].hasOwnProperty('employee')) {
      const postData: IADPMapWorker = {
        user_id: mappedUsers[id].employee.id,
        associateOID: associateOID,
      };

      setCurrentMapUser(id);

      adpWorkerMutateAsync(postData).then(() => {
        setCurrentMapUser(null);
        refetch();
      });
    }
  };

  const onUnMapADPWorker = ({ id, associateOID, selected_user }: any) => {
    setCurrentMapUser(id);

    const postData: IAdpWfnWorkUnmap = {
      associateOID: associateOID,
    };
    adpWfnWorkerUnmapMutateAsync(postData).then(() => {
      setCurrentMapUser(null);
      refetch();
      setMappedUsers((prevValue: any) => ({
        ...prevValue,
        [selected_user.id]: null,
      }));
    });
  };

  const onImportWorkers = (selectedIds: Array<number>) => {
    const postData: IADPWorkers = {
      adp_employee_ids: selectedIds,
    };
    adpImportWorkerMutateAsync(postData).then(() => {
      refetch();
    });
  };

  const timeeroEmployeeLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchADPTimeeroPaginatedEmployee, { page, search });
  };

  const onSyncADPWorker = () => {
    adpSyncMutateAsync('workers');
    syncLongPollingStatus.current = 'fetching';
  };

  const handleBulkMapUnmapEmployees = (selectedRows: Array<any>, type: 'map' | 'unmap') => {
    const mappedData: Array<{ associateOID: number; user_id: number }> = [];

    if (type === 'map') {
      selectedRows.forEach(selectedRow => {
        if (mappedUsers && mappedUsers[selectedRow.original.id]) {
          mappedData.push({
            user_id: mappedUsers[selectedRow.original.id]?.employee?.id,
            associateOID: selectedRow.original.associateOID,
          });
        }
      });

      setCurrentMapUser('bulkMap');
    } else {
      selectedRows.forEach(selectedRow => {
        if (selectedRow?.original?.selected_user) {
          mappedData.push({
            user_id: selectedRow?.original?.selected_user?.id,
            associateOID: selectedRow?.original?.associateOID,
          });
        }
      });
      setCurrentMapUser('bulkUnmap');
    }

    const postData: IBulkMapUnmapADPWorkers = {
      mapping: type === 'map' ? true : false,
      adp: mappedData,
    };

    mutateAsyncBulkMapUnmapWorkers(postData).then(() => {
      refetch();
      setCurrentMapUser(null);
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IADPTimeeroEmployeeSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    const updatedSearchQuery = (previousSearchQuery: IADPTimeeroEmployeeSearchQuery) => ({
      ...previousSearchQuery,
      page_size: pageSize,
      page: 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  return (
    <Box>
      <Title>{t('integrations:adp.adpWfnWorkersMapping')}</Title>
      <Flex justifyContent="space-between">
        <Text variant="h2" fontSize="lg" ml={10} mt={4}>
          {t('integrations:mapFieldsPlatforms')}
        </Text>
        <Button
          colorScheme="secondary"
          mt={{ base: '5', md: '0' }}
          isLoading={adpSyncStatus === 'loading' || syncLongPollingStatus?.current === 'fetching'}
          onClick={onSyncADPWorker}
        >
          {t('integrations:adp.syncChangesFromAdp')}
        </Button>
      </Flex>
      <Box ml="auto" mt={4} maxWidth={{ md: '250px' }} width="100%">
        <SearchField
          inputProps={{
            placeholder: 'Search Timeero Employees',
            onChange: handleSearchInputChange,
          }}
        />
      </Box>

      <Table
        columns={columns}
        data={adpWorkerData?.payload || []}
        isLoading={adpWorkerStatus === 'loading' || isADPWorkerFetching}
        enableRowSelect
        paginationData={adpWorkerData?.meta_data?.pagination}
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        paginationStyle="rowsPerPage"
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected: any) => {
          return (
            <WorkersMappingTableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleImportWorkers={onImportWorkers}
              isLoading={adpImportWorkerStatus === 'loading'}
              mappedUsers={mappedUsers}
              handleBulkMapUnmapEmployees={handleBulkMapUnmapEmployees}
              isBulkMapBtnLoading={currentMapUser === 'bulkMap' && bulkMapUnmapWorkerStatus === 'loading'}
              isBulkUnmapBtnLoading={currentMapUser === 'bulkUnmap' && bulkMapUnmapWorkerStatus === 'loading'}
              t={t}
            />
          );
        }}
      />
    </Box>
  );
};
export default WorkersMapping;
